// Shared date parsing and formatting helpers for TrustCall analytics tabs.

/** Parse various timestamp formats (.NET /Date(ms)/, epoch, ISO) to Date. */
export function parseTsToDate(v: unknown): Date | null {
  if (v == null) return null;
  if (v instanceof Date && !isNaN(v.getTime())) return v;
  if (typeof v === "number" && isFinite(v)) {
    const ms = Math.abs(v) < 1e12 ? v * 1000 : v;
    return new Date(ms);
  }
  if (typeof v === "string") {
    const s = v.trim();
    // Handle .NET /Date(ms)/ format
    const m = /Date\((-?\d+)\)/.exec(s);
    if (m) return new Date(Number(m[1]));
    // Numeric string
    if (/^-?\d+(\.\d+)?$/.test(s)) {
      const n = Number(s);
      return new Date(Math.abs(n) < 1e12 ? n * 1000 : n);
    }
    // ISO / standard date string
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/** Safe JSON.parse — returns parsed value or null on failure. */
export function safeParse(s: string | null | undefined): unknown {
  try {
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}

/** Get the browser's IANA timezone, falling back to America/Los_Angeles. */
export function getBrowserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Los_Angeles";
  } catch {
    return "America/Los_Angeles";
  }
}

/** Format a Date to a local date+time string with timezone. */
export function formatLocal(dt: Date | null): string {
  if (!dt) return "\u2014";
  try {
    const tz = getBrowserTimeZone();
    return dt.toLocaleString("en-US", {
      timeZone: tz, year: "numeric", month: "numeric", day: "numeric",
      hour: "numeric", minute: "numeric", second: "numeric", hour12: true,
    });
  } catch {
    return dt.toLocaleString();
  }
}

/** Format a Date to a local date-only string. */
export function formatLocalDateOnly(dt: Date | null): string {
  if (!dt) return "\u2014";
  try {
    const tz = getBrowserTimeZone();
    return dt.toLocaleDateString("en-US", { timeZone: tz });
  } catch {
    return dt.toLocaleDateString();
  }
}

/** Convert a Date to a UTC ISO date string (YYYY-MM-DD). Used for day-key grouping (e.g. VerizonAnalyticsTab). */
export function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Convert a Date to a local ISO date string (YYYY-MM-DD). Use for <input type="date"> values and user-facing date ranges. */
export function toLocalIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
